import React from 'react';
import styled, { keyframes } from 'styled-components';

export type StatusTone = 'info' | 'warning' | 'danger' | 'success';

export interface StatusBarItem {
    id: string;
    tone: StatusTone;
    message: string;
}

interface StatusBarProps {
    items: StatusBarItem[];
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

const ToastContainer = styled.div`
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 900;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 360px;
`;

const Toast = styled.div<{ $tone: StatusTone }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    animation: ${slideIn} 0.2s ease;
    color: ${({ $tone }) => {
        switch ($tone) {
            case 'warning': return '#92400e';
            case 'danger': return '#b91c1c';
            case 'success': return '#166534';
            default: return '#555';
        }
    }};
    background: ${({ $tone }) => {
        switch ($tone) {
            case 'warning': return '#fefce8';
            case 'danger': return '#fef2f2';
            case 'success': return '#f0fdf4';
            default: return '#fff';
        }
    }};
    border: 1px solid ${({ $tone }) => {
        switch ($tone) {
            case 'warning': return '#fde68a';
            case 'danger': return '#fecaca';
            case 'success': return '#bbf7d0';
            default: return '#e0e0e0';
        }
    }};
`;

export const StatusBar: React.FC<StatusBarProps> = ({ items }) => {
    if (!items.length) {
        return null;
    }

    return (
        <ToastContainer>
            {items.map(item => (
                <Toast key={item.id} $tone={item.tone}>
                    {item.message}
                </Toast>
            ))}
        </ToastContainer>
    );
};
